import React, { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Flag, History } from "lucide-react"
import { toast } from "sonner"
import api from "../../services/api"
import { ConfirmDialog } from "../ConfirmDialog"
import { Badge } from "../ui/badge"
import { Button } from "../ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Skeleton } from "../ui/skeleton"
import { Switch } from "../ui/switch"

const clone = (value) => JSON.parse(JSON.stringify(value))

const emptyChannel = {
  enabled: false,
  rolloutBasisPoints: 0,
  minimumVersion: null,
  minimumBuild: null,
}

function canonicalChannel(channel) {
  return {
    ...emptyChannel,
    ...channel,
  }
}

export function validateFlagChannel(channel) {
  if (
    !Number.isSafeInteger(channel?.rolloutBasisPoints) ||
    channel.rolloutBasisPoints < 0 ||
    channel.rolloutBasisPoints > 10000
  ) {
    return "Rollout must be between 0% and 100%."
  }
  if (channel.enabled && channel.rolloutBasisPoints === 0) {
    return "An enabled rollout must be greater than 0%."
  }
  if (
    channel.minimumVersion &&
    !/^\d+(?:\.\d+)*$/.test(channel.minimumVersion)
  ) {
    return "Minimum version must contain only dot-separated numbers."
  }
  if (
    channel.minimumBuild !== null &&
    channel.minimumBuild !== undefined &&
    channel.minimumBuild !== "" &&
    (!Number.isSafeInteger(channel.minimumBuild) || channel.minimumBuild < 0)
  ) {
    return "Minimum build must be a nonnegative whole number."
  }
  return null
}

export function changeNeedsConfirmation(current, next) {
  if (!current || !next) return false
  const productionIsStarting =
    !current.production.enabled && next.production.enabled
  const anyChannelIsPausing =
    (current.production.enabled && !next.production.enabled) ||
    (current.testFlight.enabled && !next.testFlight.enabled)
  return productionIsStarting || anyChannelIsPausing
}

function ChannelEditor({ featureKey, channelKey, title, value, onChange }) {
  const prefix = `${featureKey}-${channelKey}`

  const update = (field, nextValue) => {
    onChange({ ...value, [field]: nextValue })
  }

  return (
    <div className="space-y-4 rounded-lg border p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="font-semibold">{title}</h4>
          <p className="text-xs text-muted-foreground">
            Enabled state and rollout apply only to this distribution channel.
          </p>
        </div>
        <Switch
          id={`${prefix}-enabled`}
          checked={value.enabled}
          onCheckedChange={(checked) => update("enabled", checked)}
          aria-label={`${title} enabled`}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-rollout`}>Rollout Percentage</Label>
          <Input
            id={`${prefix}-rollout`}
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={value.rolloutBasisPoints / 100}
            onChange={(event) =>
              update(
                "rolloutBasisPoints",
                Math.round(Number(event.target.value) * 100)
              )
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-version`}>Minimum Version</Label>
          <Input
            id={`${prefix}-version`}
            value={value.minimumVersion ?? ""}
            placeholder="Optional"
            onChange={(event) =>
              update("minimumVersion", event.target.value.trim() || null)
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${prefix}-build`}>Minimum Build</Label>
          <Input
            id={`${prefix}-build`}
            type="number"
            min="0"
            step="1"
            value={value.minimumBuild ?? ""}
            placeholder="Optional"
            onChange={(event) =>
              update(
                "minimumBuild",
                event.target.value === "" ? null : Number(event.target.value)
              )
            }
          />
        </div>
      </div>
    </div>
  )
}

function FeatureEditor({
  featureKey,
  definition,
  canonical,
  draft,
  onDraftChange,
  onSave,
  isSaving,
}) {
  const productionError = validateFlagChannel(draft.production)
  const testFlightError = validateFlagChannel(draft.testFlight)
  const unchanged = JSON.stringify(canonical) === JSON.stringify(draft)

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Flag className="h-5 w-5" />
              {definition.displayName}
            </CardTitle>
            <CardDescription>{definition.description}</CardDescription>
          </div>
          <div className="flex gap-2">
            <Badge variant={draft.production.enabled ? "success" : "muted"}>
              App Store {draft.production.enabled ? "On" : "Off"}
            </Badge>
            <Badge variant={draft.testFlight.enabled ? "success" : "muted"}>
              TestFlight {draft.testFlight.enabled ? "On" : "Off"}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-900">
          {definition.safetyNote}
        </p>
        <div className="grid gap-4 xl:grid-cols-2">
          <ChannelEditor
            featureKey={featureKey}
            channelKey="production"
            title="App Store"
            value={draft.production}
            onChange={(production) => onDraftChange({ ...draft, production })}
          />
          <ChannelEditor
            featureKey={featureKey}
            channelKey="testFlight"
            title="TestFlight"
            value={draft.testFlight}
            onChange={(testFlight) => onDraftChange({ ...draft, testFlight })}
          />
        </div>
        {(productionError || testFlightError) && (
          <p className="text-sm text-destructive" role="alert">
            {productionError || testFlightError}
          </p>
        )}
        <div className="flex justify-end border-t pt-4">
          <Button
            onClick={onSave}
            disabled={
              isSaving ||
              unchanged ||
              Boolean(productionError || testFlightError)
            }
          >
            {isSaving ? "Saving…" : "Save Rollout"}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function FeatureFlagConfigPanel() {
  const queryClient = useQueryClient()
  const [drafts, setDrafts] = useState({})
  const [pendingConfirmation, setPendingConfirmation] = useState(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ["feature-flags"],
    queryFn: () => api.getFeatureFlags(),
  })

  useEffect(() => {
    if (!data?.features) return
    setDrafts(
      Object.fromEntries(
        Object.entries(data.features).map(([key, feature]) => [
          key,
          {
            production: canonicalChannel(feature.production),
            testFlight: canonicalChannel(feature.testFlight),
          },
        ])
      )
    )
  }, [data])

  const saveMutation = useMutation({
    mutationFn: ({ key, draft }) =>
      api.updateFeatureFlag(key, draft, data.revision),
    onSuccess: (canonical) => {
      queryClient.setQueryData(["feature-flags"], canonical)
      setPendingConfirmation(null)
      toast.success("Feature rollout saved")
    },
    onError: (mutationError) => {
      setPendingConfirmation(null)
      if (mutationError.status === 409) {
        queryClient.invalidateQueries({ queryKey: ["feature-flags"] })
        toast.error(
          "Configuration changed elsewhere. Reloaded the latest values."
        )
        return
      }
      toast.error(`Failed to save: ${mutationError.message}`)
    },
  })

  const sortedAudit = useMemo(() => data?.audit ?? [], [data?.audit])

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Remote Feature Flags</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Remote Feature Flags</CardTitle>
          <CardDescription>
            Feature flags could not be loaded: {error.message}
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const requestSave = (key) => {
    const draft = drafts[key]
    const current = {
      production: canonicalChannel(data.features[key].production),
      testFlight: canonicalChannel(data.features[key].testFlight),
    }
    if (changeNeedsConfirmation(current, draft)) {
      setPendingConfirmation({ key, draft: clone(draft) })
    } else {
      saveMutation.mutate({ key, draft })
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">Remote Feature Flags</h2>
        <p className="text-sm text-muted-foreground">
          Ship dormant code, then control enrollment independently for App Store
          and TestFlight. Changes reach online clients on their next refresh.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Revision {data.revision} · Updated{" "}
          {new Date(data.updatedAt).toLocaleString()}
        </p>
      </div>

      {Object.keys(data.definitions).map((key) =>
        drafts[key] ? (
          <FeatureEditor
            key={key}
            featureKey={key}
            definition={data.definitions[key]}
            canonical={{
              production: canonicalChannel(data.features[key].production),
              testFlight: canonicalChannel(data.features[key].testFlight),
            }}
            draft={drafts[key]}
            onDraftChange={(draft) =>
              setDrafts((current) => ({ ...current, [key]: draft }))
            }
            onSave={() => requestSave(key)}
            isSaving={saveMutation.isPending}
          />
        ) : null
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <History className="h-5 w-5" /> Recent Changes
          </CardTitle>
          <CardDescription>
            The most recent server-side rollout updates, newest first.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sortedAudit.length === 0 ? (
            <p className="text-sm text-muted-foreground">No changes yet.</p>
          ) : (
            <div className="space-y-3">
              {sortedAudit.map((entry) => (
                <div
                  key={`${entry.revision}-${entry.key}`}
                  className="flex flex-col justify-between gap-1 rounded-md border p-3 text-sm sm:flex-row"
                >
                  <div>
                    <div className="font-medium">
                      Revision {entry.revision} ·{" "}
                      {data.definitions[entry.key]?.displayName ?? entry.key}
                    </div>
                    <div className="text-muted-foreground">
                      App Store {entry.after.production.enabled ? "on" : "off"}
                      {" "}at {entry.after.production.rolloutBasisPoints / 100}% ·{" "}
                      TestFlight {entry.after.testFlight.enabled ? "on" : "off"}
                      {" "}at {entry.after.testFlight.rolloutBasisPoints / 100}%
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(entry.at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={pendingConfirmation !== null}
        onOpenChange={(open) => !open && setPendingConfirmation(null)}
        title="Apply this rollout change?"
        description="Pausing stops only new enrollment. Existing and partially migrated users continue. App Store enablement begins a production rollout, and offline clients will not receive the change until they reconnect."
        confirmLabel="Apply Rollout"
        variant="default"
        onConfirm={() =>
          pendingConfirmation &&
          saveMutation.mutate({
            key: pendingConfirmation.key,
            draft: pendingConfirmation.draft,
          })
        }
      />
    </div>
  )
}

export default FeatureFlagConfigPanel
